(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-cart-drawer';
  var BODY_LOCK_CLASS = 'oddit-cart-drawer-open';

  var state = {
    section: null,
    abortController: null,
    isOpen: false,
    lastFocusedElement: null,
    cart: null
  };

  // Block overlapping mutations so out-of-order section renders can't clobber
  // each other.
  var isMutating = false;

  // -----------------------------------------------------------------------
  // Section helpers
  // -----------------------------------------------------------------------
  function getSection() {
    return state.section || document.querySelector(SECTION_SELECTOR);
  }

  function getSectionId() {
    var section = getSection();
    return section ? section.id : null;
  }

  // -----------------------------------------------------------------------
  // Cart AJAX API
  //
  // Every mutation asks Shopify to re-render this section (Section Rendering
  // API) so the drawer markup stays a single source of truth: Liquid renders
  // titles (incl. the base_product_title metafield), prices, swatches, the
  // empty/filled/footer states, etc. The JS only swaps the rendered HTML in.
  // -----------------------------------------------------------------------
  function getCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('Failed to fetch cart');
      return res.json();
    });
  }

  function parseError(res) {
    return res.json().then(function (err) { throw err; });
  }

  // POST to a /cart/*.js endpoint requesting this section's HTML, then swap
  // the DOM and refresh derived state.
  function postCartChange(url, body) {
    if (isMutating) return Promise.resolve(state.cart);
    isMutating = true;

    body.sections = [getSectionId()];
    body.sections_url = window.location.pathname;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })
      .then(function (res) { return res.ok ? res.json() : parseError(res); })
      .then(function (data) {
        renderSectionFromResponse(data);
        return resolveCart(data);
      })
      .then(function (cart) {
        afterRender(cart);
        return cart;
      })
      .then(
        function (cart) { isMutating = false; return cart; },
        function (err) { isMutating = false; clearLoadingStates(); throw err; }
      );
  }

  // change.js responds with the cart object; add.js responds with the added
  // item(s). When we don't already have a full cart, fetch it for the
  // count/upsell logic + the cart:updated event.
  function resolveCart(data) {
    if (data && data.items && typeof data.item_count === 'number') {
      return Promise.resolve(data);
    }
    return getCart();
  }

  function addToCart(variantId, quantity, properties) {
    return postCartChange('/cart/add.js', {
      id: variantId,
      quantity: quantity == null ? 1 : quantity,
      properties: properties || {}
    });
  }

  function addMultipleToCart(items) {
    return postCartChange('/cart/add.js', { items: items });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return postCartChange('/cart/change.js', { id: lineItemKey, quantity: quantity });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return postCartChange('/cart/change.js', { id: lineItemKey, selling_plan: sellingPlanId });
  }

  // No mutation — re-render from the current server cart (used on open).
  function refreshCart() {
    var id = getSectionId();
    var sectionFetch = id
      ? fetch(window.location.pathname + '?sections=' + encodeURIComponent(id), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        }).then(function (res) { return res.ok ? res.json() : null; })
      : Promise.resolve(null);

    return Promise.all([getCart(), sectionFetch]).then(function (results) {
      var cart = results[0];
      var sections = results[1];
      if (sections && id && sections[id]) renderSection(sections[id]);
      afterRender(cart);
      return cart;
    });
  }

  // -----------------------------------------------------------------------
  // Section rendering — swap the panel's inner DOM from rendered HTML.
  // The root + panel elements are preserved so open state, focus, scroll
  // lock, and the slide transition are untouched.
  // -----------------------------------------------------------------------
  function renderSectionFromResponse(data) {
    var id = getSectionId();
    if (data && data.sections && id && data.sections[id]) {
      renderSection(data.sections[id]);
    }
  }

  function renderSection(html) {
    var section = getSection();
    if (!section || !html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var newRoot = parsed.querySelector(SECTION_SELECTOR);
    var newPanel = newRoot && newRoot.querySelector('.oddit-cart-drawer__panel');
    var livePanel = section.querySelector('.oddit-cart-drawer__panel');
    if (!newPanel || !livePanel) return;

    var hadFocusInside = livePanel.contains(document.activeElement);
    livePanel.innerHTML = newPanel.innerHTML;
    if (state.isOpen && hadFocusInside && focusIsLost()) focusPanel(section);
  }

  function afterRender(cart) {
    state.cart = cart;
    filterUpsellInCart(cart);
    // Clear the persisted expiry whenever the cart is empty so the next item
    // added starts a fresh full-duration countdown (rather than picking up
    // whatever time was left from the previous shopping session).
    if (cart && cart.item_count === 0) {
      clearReservationExpiry();
    }
    tickReservation();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  // Pending UI: dim the acted-on card/item while its request is in flight.
  // The section re-render replaces the element (clearing the class); the error
  // path clears any that are left stuck.
  function setLoading(el) {
    if (el && el.classList) el.classList.add('is-loading');
  }

  function clearLoadingStates() {
    var section = getSection();
    if (!section) return;
    section.querySelectorAll('.is-loading').forEach(function (el) {
      el.classList.remove('is-loading');
    });
  }

  // The cross-sell Liquid doesn't know what's already in the cart, so hide
  // upsell cards for products that are — and collapse the whole block (heading
  // + divider) when nothing's left to show.
  function filterUpsellInCart(cart) {
    var section = getSection();
    if (!section || !cart) return;

    var inCart = {};
    (cart.items || []).forEach(function (item) {
      inCart[String(item.product_id)] = true;
    });

    var container = section.querySelector('[data-cross-sell-container]');
    if (!container) return;

    var visible = 0;
    container.querySelectorAll('[data-cross-sell-card][data-product-id]').forEach(function (card) {
      var hide = !!inCart[card.getAttribute('data-product-id')];
      card.style.display = hide ? 'none' : '';
      if (!hide) visible += 1;
    });

    var display = visible > 0 ? '' : 'none';
    container.style.display = display;
    // Collapse the divider that sits directly before the upsell block too.
    var divider = container.previousElementSibling;
    if (divider && divider.classList.contains('oddit-cart-drawer__divider')) {
      divider.style.display = display;
    }
  }

  // -----------------------------------------------------------------------
  // Open / close / toggle
  // -----------------------------------------------------------------------
  function open() {
    var section = getSection();
    if (!section) return;
    state.section = section;

    // Clear inline display:none from FOUC prevention.
    section.style.display = '';

    state.lastFocusedElement = document.activeElement;
    section.classList.add('is-open');
    document.body.classList.add(BODY_LOCK_CLASS);
    document.body.style.overflow = 'hidden';
    state.isOpen = true;

    focusPanel(section);

    // Re-render with fresh cart data on open.
    refreshCart()
      .then(function () { if (state.isOpen && focusIsLost()) focusPanel(); })
      .catch(function () { /* swallow */ });
  }

  function close() {
    var section = getSection();
    if (!section) return;

    section.classList.remove('is-open');
    document.body.classList.remove(BODY_LOCK_CLASS);
    document.body.style.overflow = '';
    state.isOpen = false;

    if (state.lastFocusedElement && typeof state.lastFocusedElement.focus === 'function') {
      try { state.lastFocusedElement.focus(); } catch (e) { /* noop */ }
    }
  }

  function toggle() {
    if (state.isOpen) close();
    else open();
  }

  // -----------------------------------------------------------------------
  // Focus trap
  // -----------------------------------------------------------------------
  function focusIsLost() {
    var active = document.activeElement;
    return !active || active === document.body || active === document.documentElement;
  }

  function focusPanel(section) {
    section = section || getSection();
    if (!section) return;
    var panel = section.querySelector('.oddit-cart-drawer__panel');
    if (!panel) return;
    if (panel.getAttribute('tabindex') === null) panel.setAttribute('tabindex', '-1');
    try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }

    if (document.activeElement !== panel) {
      requestAnimationFrame(function () {
        if (!state.isOpen || !document.contains(panel) || !focusIsLost()) return;
        try { panel.focus({ preventScroll: true }); } catch (err) { panel.focus(); }
      });
    }
  }

  function trapFocus(e) {
    if (!state.isOpen || e.key !== 'Tab') return;
    var section = getSection();
    if (!section) return;

    var focusable = section.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    var panel = section.querySelector('.oddit-cart-drawer__panel');

    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // -----------------------------------------------------------------------
  // Delegated interactions — bound once on the (stable) section root so they
  // survive every section re-render.
  // -----------------------------------------------------------------------
  function handleClick(e) {
    // The cart page (.oddit-main-cart) and the global header drawer both carry
    // .oddit-cart-drawer, so getSection()'s document-wide fallback can resolve
    // to the wrong instance. The listener is bound per-section, so e.currentTarget
    // is authoritative — point state.section at it so every section-scoped helper
    // (render, getSectionId, querying) acts on the instance that was clicked.
    var section = (e.currentTarget && e.currentTarget.classList &&
      e.currentTarget.classList.contains('oddit-cart-drawer')) ? e.currentTarget : getSection();
    if (!section) return;
    state.section = section;

    // Close button.
    if (e.target.closest('[data-cart-close], .oddit-cart-drawer__close')) {
      close();
      return;
    }

    // Quantity +/- (attribute names match the Liquid line-item markup).
    var qtyBtn = e.target.closest('[data-quantity-increase], [data-quantity-decrease]');
    if (qtyBtn) {
      if (isMutating) return;
      var key = qtyBtn.getAttribute('data-line-item-key');
      var isIncrease = qtyBtn.hasAttribute('data-quantity-increase');
      var valueEl = section.querySelector('[data-quantity-input][data-line-item-key="' + key + '"]');
      var current = valueEl ? (parseInt(valueEl.textContent, 10) || 1) : 1;
      var next = isIncrease ? current + 1 : Math.max(current - 1, 0);
      setLoading(qtyBtn.closest('[data-line-item]'));
      updateLineItemQuantity(key, next).catch(function () { /* swallow */ });
      return;
    }

    // Remove line.
    var removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      if (isMutating) return;
      setLoading(removeBtn.closest('[data-line-item]'));
      removeFromCart(removeBtn.getAttribute('data-line-item-key')).catch(function () { /* swallow */ });
      return;
    }

    // Cross-sell / upsell add.
    var addBtn = e.target.closest(
      '.oddit-cart-drawer__rec-add[data-variant-id], .oddit-cart-drawer__upsell-add[data-variant-id], .oddit-cart-drawer__upsell-cta[data-variant-id]'
    );
    if (addBtn) {
      if (isMutating) return;
      var variantId = addBtn.getAttribute('data-variant-id');
      if (variantId) {
        setLoading(addBtn.closest('[data-cross-sell-card]') || addBtn);
        addToCart(Number(variantId)).catch(function () { /* swallow */ });
      }
      return;
    }

    // Subscription switch.
    var switchBtn = e.target.closest('.oddit-cart-drawer__switch-cta[data-selling-plan-id]');
    if (switchBtn) {
      if (isMutating) return;
      var planId = switchBtn.getAttribute('data-selling-plan-id');
      var lineKey = switchBtn.getAttribute('data-line-key');
      if (planId && lineKey) {
        setLoading(switchBtn.closest('[data-line-item]') || switchBtn);
        updateLineItemToSubscription(lineKey, planId).catch(function () { /* swallow */ });
      }
      return;
    }

    var checkoutBtn = e.target.closest('.oddit-cart-drawer__checkout-cta, [data-cart-checkout]');
    if (checkoutBtn) {
      setCheckoutLoading(checkoutBtn);
      // fall through — let the native submit proceed.
      return;
    }

    // Click on the dimmed backdrop (outside the panel) closes — overlay drawer
    // only. On the static cart page the section is a normal block (never open),
    // so the isOpen guard keeps background clicks from touching body scroll lock.
    if (e.target === section && state.isOpen) close();
  }

  function setCheckoutLoading(btn) {
    if (!btn || !btn.classList || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    // Keep the button focusable/labelled for a11y but block double-submit.
    btn.setAttribute('aria-busy', 'true');
  }

  function handleCheckoutSubmit(e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('.oddit-cart-drawer__checkout-form')) return;
    // Same instance-routing as handleClick — act on the section that owns this form.
    if (e.currentTarget && e.currentTarget.classList &&
      e.currentTarget.classList.contains('oddit-cart-drawer')) {
      state.section = e.currentTarget;
    }
    var btn = form.querySelector('[data-cart-checkout]');
    setCheckoutLoading(btn);
    // Intentionally NOT preventing default — the native cart-form submit handles
    // navigation to checkout with the current cart state.
  }

  // -----------------------------------------------------------------------
  // Reservation countdown timer
  //
  // The reservation banner is re-rendered server-side on every cart mutation
  // (Section Rendering API), so its timer element is recreated frequently.
  // We keep a single interval running and re-query the element each tick, and
  // persist the expiry in localStorage so the countdown stays continuous
  // across re-renders, drawer reopen, and page reloads. It restarts when it
  // reaches zero.
  // -----------------------------------------------------------------------
  var RESERVATION_STORAGE_KEY = 'oddit-cart-reservation-expiry';
  var reservationInterval = null;

  function getReservationTimerEl() {
    var section = getSection();
    return section ? section.querySelector('[data-cart-reservation-timer]') : null;
  }

  function reservationDurationMs() {
    var section = getSection();
    var banner = section && section.querySelector('[data-cart-reservation]');
    var minutes = banner ? parseFloat(banner.getAttribute('data-reservation-minutes')) : 0;
    if (!minutes || minutes <= 0) minutes = 10;
    return Math.round(minutes * 60 * 1000);
  }

  // Returns the expiry timestamp, creating/persisting one when missing OR
  // when the stored value has already lapsed. Without the lapsed-reset path
  // the timer would render frozen at 0:00 forever once it ran out (which is
  // what shoppers reported seeing).
  function getReservationExpiry() {
    var stored = 0;
    try { stored = parseInt(localStorage.getItem(RESERVATION_STORAGE_KEY), 10) || 0; }
    catch (e) { stored = 0; }
    if (!stored || stored <= Date.now()) {
      stored = Date.now() + reservationDurationMs();
      try { localStorage.setItem(RESERVATION_STORAGE_KEY, String(stored)); }
      catch (e) { /* storage unavailable — timer still ticks for this session */ }
    }
    return stored;
  }

  // Drop the persisted expiry so the next call to getReservationExpiry()
  // creates a fresh one. Called when the cart transitions to empty.
  function clearReservationExpiry() {
    try { localStorage.removeItem(RESERVATION_STORAGE_KEY); }
    catch (e) { /* storage unavailable — noop */ }
  }

  function formatCountdown(ms) {
    var totalSeconds = Math.max(0, Math.round(ms / 1000));
    var m = Math.floor(totalSeconds / 60);
    var s = totalSeconds % 60;
    return (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
  }

  function tickReservation() {
    var span = getReservationTimerEl();
    if (!span) return; // banner not rendered (disabled / empty cart)
    var remaining = getReservationExpiry() - Date.now();
    span.textContent = formatCountdown(remaining);
    if (remaining <= 0) stopReservationTimer(); // reached 0:00 — stop, don't repeat
  }

  function startReservationTimer() {
    tickReservation();
    if (reservationInterval) return;
    reservationInterval = setInterval(tickReservation, 1000);
  }

  function stopReservationTimer() {
    if (reservationInterval) {
      clearInterval(reservationInterval);
      reservationInterval = null;
    }
  }

  // -----------------------------------------------------------------------
  // Init / teardown
  // -----------------------------------------------------------------------
  function initSection(section) {
    if (!section || section.dataset.odditInit === '1') return;
    section.dataset.odditInit = '1';

    state.section = section;
    state.abortController = new AbortController();
    var signal = state.abortController.signal;

    // All cart interactions are delegated on the root so they survive
    // section re-renders.
    section.addEventListener('click', handleClick, { signal: signal });

    // BEB-70. Hook the checkout form's submit (capture not needed; submit
    // bubbles). This fires for click, keyboard Enter and programmatic submit,
    // which the click handler alone missed — the cause of the "sometimes this
    // doesn't work" report.
    section.addEventListener('submit', handleCheckoutSubmit, { signal: signal });

    document.addEventListener('keydown', function (e) {
      if (state.isOpen && e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    }, { signal: signal });

    document.addEventListener('keydown', trapFocus, { signal: signal });

    // The server already rendered the drawer for the current cart, so just
    // sync derived state (header badge via cart:updated, upsell filtering)
    // without re-fetching the section.
    getCart().then(afterRender).catch(function () { /* swallow */ });

    startReservationTimer();
  }

  function destroySection(section) {
    if (!section) return;
    section.dataset.odditInit = '';
    stopReservationTimer();
    if (state.abortController) {
      try { state.abortController.abort(); } catch (e) { /* noop */ }
      state.abortController = null;
    }
    if (state.section === section) {
      state.section = null;
    }
  }

  function initAll(root) {
    var scope = root || document;
    scope.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });

  window.addEventListener('pageshow', function () {
    var section = getSection();
    if (!section) return;
    section.querySelectorAll('[data-cart-checkout].is-loading').forEach(function (btn) {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    });
  });

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------
  window.OdditCartDrawer = {
    open: open,
    close: close,
    toggle: toggle,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart
  };
})();
